import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, MapPin, MoreVertical, Users, Clock, FolderOpen, CheckCircle2, FileText, LayoutGrid, List, ArrowRight, Archive } from "lucide-react";
import api, { apiError } from "@/lib/api";
import NewClientModal from "@/components/NewClientModal";
import ClientFicheModal from "@/components/ClientFicheModal";
import ClientPonctuelSheet from "@/components/accounting/ClientPonctuelSheet";
import { frDate, clientInternalStatusOf, clientServices, prestationLabel, isCmClient, isGraphismeClient, isWebClient } from "@/lib/constants";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PASTEL = {
  blue: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD" },
  amber: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  green: { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0" },
  rose: { bg: "#FFE4E6", text: "#BE123C", border: "#FECDD3" },
  purple: { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
};
const SERVICE_PILL = {
  communication: { label: "Community Management", ...PASTEL.green },
  graphisme: { label: "Graphisme", ...PASTEL.amber },
  web: { label: "Web Design", ...PASTEL.purple },
  les_deux: null,
};
const servicePills = (c) => {
  const svcs = clientServices(c);
  const out = [];
  svcs.forEach((s) => {
    if (s === "les_deux") { out.push(SERVICE_PILL.communication, SERVICE_PILL.graphisme); return; }
    if (s === "communication" || s === "community_management") out.push(SERVICE_PILL.communication);
    else if (s === "site_web" || s === "maintenance_web" || s === "web") out.push(SERVICE_PILL.web);
    else if (SERVICE_PILL[s]) out.push(SERVICE_PILL[s]);
    else out.push({ label: prestationLabel(s), ...PASTEL.blue });
  });
  return [...new Map(out.map((p) => [p.label, p])).values()];
};
const STATUS_BADGE = {
  actif: { label: "Actif", ...PASTEL.green },
  pause: { label: "En pause", ...PASTEL.amber },
  prospect: { label: "Prospect", ...PASTEL.purple },
  ancien: { label: "Ancien", bg: "#F1F5F9", text: "#475569", border: "#E2E8F0" },
};
/* % de remplissage de la fiche client (champs clés de ClientFicheModal) */
const FICHE_CHECKS = [
  (c) => c.name, (c) => c.sector, (c) => c.email, (c) => c.phone,
  (c) => c.contact_first_name, (c) => c.contact_last_name,
  (c) => c.address, (c) => c.zip_code, (c) => c.city,
  (c) => c.billing_email || c.email,
  (c) => c.client_type === "particulier" || c.billing_siret,
  (c) => c.brand?.logo_url,
  (c) => (c.services?.length || c.service_type),
  (c) => (c.objective_month || 0) > 0,
];
const fichePct = (c) => Math.round((FICHE_CHECKS.filter((f) => { try { return !!f(c); } catch { return false; } }).length / FICHE_CHECKS.length) * 100);
const ficheColor = (p) => (p >= 90 ? "#15803D" : p >= 60 ? "#B45309" : "#BE123C");

function Kpi({ testid, icon: Icon, pastel, value, label, sub }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-4 py-4 flex gap-3 items-center" data-testid={testid}>
      <span className="w-11 h-11 rounded-2xl flex items-center justify-center border shrink-0"
        style={{ background: pastel.bg, color: pastel.text, borderColor: pastel.border }}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-[#475569]">{label}</p>
        <p className="font-heading text-2xl font-bold text-[#0F172A] leading-tight flex items-baseline gap-2">
          {value}
          {sub && <span className="text-[11px] font-medium" style={{ color: sub.color || "#15803D" }} data-testid={`${testid}-sub`}>{sub.text}</span>}
        </p>
      </div>
    </div>
  );
}

function ClientCard({ c, i, onOpen, onFiche, onPonctuel, onToggleArchive, projetsCount }) {
  const st = STATUS_BADGE[c.status || "actif"] || STATUS_BADGE.actif;
  const pills = servicePills(c);
  const pending = c.stats?.pending_count ?? 0;
  const projets = projetsCount[c.id] || 0;
  const pct = fichePct(c);
  return (
    <div data-testid={`client-card-${c.id}`}
      className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5 hover:shadow-md hover:border-neutral-300 transition-all duration-200 animate-fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-2xl shrink-0 overflow-hidden border border-black/5"
          style={{ background: c.color }} data-testid={`client-logo-${c.id}`}>
          {c.brand?.logo_url ? <img src={c.brand.logo_url} className="w-full h-full object-cover" alt="" /> : c.name.slice(0, 1)}
        </div>
        <div className="flex-1" />
        <span className="text-[10px] font-semibold rounded-full px-2.5 py-1 border whitespace-nowrap"
          style={{ background: st.bg, color: st.text, borderColor: st.border }} data-testid={`client-status-${c.id}`}>
          {st.label}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`client-menu-${c.id}`} className="p-1 rounded-lg text-neutral-300 hover:text-neutral-600 hover:bg-neutral-100 transition-colors">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid={`client-menu-fiche-${c.id}`} onClick={() => onFiche(c)}>Fiche client (paramètres)</DropdownMenuItem>
            <DropdownMenuItem data-testid={`client-menu-open-${c.id}`} onClick={() => onOpen(c)}>Ouvrir le dashboard client</DropdownMenuItem>
            {c.kind === "ponctuel" && !c.archived && (
              <DropdownMenuItem data-testid={`client-menu-ponctuel-${c.id}`} onClick={() => onPonctuel(c)}>Fiche ponctuelle</DropdownMenuItem>
            )}
            {!c.archived && (
              <DropdownMenuItem data-testid={`client-menu-archive-${c.id}`} onClick={() => onToggleArchive(c)}>
                <Archive className="w-3.5 h-3.5 mr-2" /> Archiver
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <h3 className="font-heading font-bold text-[#0F172A] mt-3 truncate">{c.name}</h3>
      <p className="text-xs text-neutral-400 truncate">{c.sector || (c.kind === "ponctuel" ? "Client non récurrent" : "—")}</p>
      {c.city && <p className="text-xs text-neutral-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {c.city}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {pills.map((p) => (
          <span key={p.label} className="text-[10px] font-medium px-2 py-0.5 rounded-full border"
            style={{ background: p.bg, color: p.text, borderColor: p.border }} data-testid={`client-svc-${c.id}`}>
            {p.label}
          </span>
        ))}
      </div>
      {/* Remplissage de la fiche */}
      <div className="mt-3" data-testid={`client-fichepct-${c.id}`}>
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-neutral-400">Fiche complétée</span>
          <span className="font-semibold" style={{ color: ficheColor(pct) }}>{pct} %</span>
        </div>
        <div className="h-1 bg-neutral-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ficheColor(pct) }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div className="bg-neutral-50 border border-neutral-100 rounded-xl px-3 py-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-neutral-400 shrink-0" />
          <div>
            <p className={`text-sm font-bold leading-tight ${pending ? "text-[#B45309]" : "text-[#0F172A]"}`} data-testid={`client-pending-${c.id}`}>{pending}</p>
            <p className="text-[10px] text-neutral-400 leading-tight">Publication{pending > 1 ? "s" : ""} à valider</p>
          </div>
        </div>
        <div className="bg-neutral-50 border border-neutral-100 rounded-xl px-3 py-2 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-neutral-400 shrink-0" />
          <div>
            <p className="text-sm font-bold leading-tight text-[#0F172A]" data-testid={`client-projets-${c.id}`}>{projets}</p>
            <p className="text-[10px] text-neutral-400 leading-tight">Projet{projets > 1 ? "s" : ""} en cours</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button data-testid={`client-fiche-${c.id}`} onClick={() => onFiche(c)}
          className="flex items-center justify-center gap-1.5 border border-[#E2E8F0] hover:border-neutral-300 hover:bg-neutral-50 text-[#0F172A] text-xs font-semibold rounded-xl px-3 py-2.5 transition-colors">
          Fiche client
        </button>
        <button data-testid={`client-open-${c.id}`} onClick={() => onOpen(c)}
          className="flex items-center justify-center gap-1.5 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0369A1] text-xs font-semibold rounded-xl px-3 py-2.5 transition-colors">
          Ouvrir le client <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [gprojs, setGprojs] = useState([]);
  const [wprojs, setWprojs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [search, setSearch] = useState("");
  const [fStatut, setFStatut] = useState("");
  const [fService, setFService] = useState("");
  const [sort, setSort] = useState("recents");
  const [view, setView] = useState("grid");
  const [open, setOpen] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [ficheClient, setFicheClient] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/clients?include_archived=true").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/graphisme/projects").then((r) => setGprojs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/web/projects").then((r) => setWprojs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/publications").then((r) => setPubs(r.data || [])).catch(() => {}); }, []);

  const onOpen = (c) => navigate(`/clients/${c.id}`); // « Ouvrir le client » = dashboard client (workspace)
  const onToggleArchive = async (c) => {
    if (c.archived) return; // pas d'endpoint de restauration côté backend
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client archivé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const projetsCount = useMemo(() => {
    const FINAL = ["paye", "livre", "termine", "archive"];
    const m = {};
    gprojs.filter((p) => !FINAL.includes(p.status)).forEach((p) => { m[p.client_id] = (m[p.client_id] || 0) + 1; });
    wprojs.filter((p) => !["livre", "termine", "archive"].includes(p.stage || p.op_status)).forEach((p) => { m[p.client_id] = (m[p.client_id] || 0) + 1; });
    return m;
  }, [gprojs, wprojs]);

  const month = new Date().toISOString().slice(0, 7);
  const all = clients || [];
  const actifs = all.filter((c) => !c.archived);
  const nouveaux = actifs.filter((c) => (c.created_at || "").startsWith(month)).length;
  const totalProjets = Object.values(projetsCount).reduce((a, b) => a + b, 0);
  const objectifMois = actifs.reduce((a, c) => a + (c.objective_month || 0), 0);
  const faitMois = pubs.filter((p) => (p.date || "").startsWith(month) && ["valide", "programme", "publie"].includes(p.status)).length;
  const pctObjectif = objectifMois > 0 ? Math.min(100, Math.round((faitMois / objectifMois) * 100)) : null;

  const filtered = useMemo(() => {
    let list = fStatut === "archives" ? all.filter((c) => c.archived) : actifs;
    if (fStatut && fStatut !== "archives") list = list.filter((c) => (c.status || "actif") === fStatut);
    if (fService) list = list.filter((c) => {
      if (fService === "les_deux") return isCmClient(c) && isGraphismeClient(c);
      if (fService === "communication") return isCmClient(c);
      if (fService === "graphisme") return isGraphismeClient(c);
      if (fService === "web") return isWebClient(c);
      return true;
    });
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => [c.name, c.city, c.sector].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
    if (sort === "nom") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "validation") list = [...list].sort((a, b) => (b.stats?.pending_count || 0) - (a.stats?.pending_count || 0));
    else list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [all, actifs, fStatut, fService, search, sort]);

  const selectCls = "text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-neutral-700 hover:border-neutral-300 transition-colors min-w-[140px]";

  return (
    <div className="space-y-4" data-testid="clients-page">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Clients</h1>
          <p className="text-neutral-500 text-xs mt-1">Gérez vos clients, suivez leurs projets et accédez rapidement à toutes les informations.</p>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-[#64748B] italic text-sm leading-snug text-right hidden md:block">« Des marques uniques,<br />des collaborations durables. »</p>
          <button data-testid="add-client-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl px-4 py-2 text-xs font-semibold transition-colors">
            <Plus className="w-3.5 h-3.5" /> Nouveau client
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="clients-kpis">
        <Kpi testid="kpi-clients-actifs" icon={Users} pastel={PASTEL.blue} value={actifs.length} label="Clients actifs"
          sub={nouveaux > 0 ? { text: `+${nouveaux} ce mois-ci` } : null} />
        <Kpi testid="kpi-nouveaux" icon={Clock} pastel={PASTEL.amber} value={nouveaux} label="Nouveaux clients"
          sub={{ text: "ce mois-ci", color: "#B45309" }} />
        <Kpi testid="kpi-projets" icon={FolderOpen} pastel={PASTEL.green} value={totalProjets} label="Projets en cours"
          sub={{ text: "graphisme + web", color: "#15803D" }} />
        <Kpi testid="kpi-objectif" icon={CheckCircle2} pastel={PASTEL.rose} value={pctObjectif != null ? `${pctObjectif} %` : "—"} label="Objectif du mois"
          sub={{ text: objectifMois ? `${faitMois}/${objectifMois} contenus validés` : "aucun objectif défini", color: "#BE123C" }} />
      </div>

      {/* Barre de recherche + filtres */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-3 py-2.5 flex flex-wrap items-end gap-x-4 gap-y-2" data-testid="clients-filterbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-neutral-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client, une ville, un secteur…"
            className="w-full text-xs border border-[#E2E8F0] rounded-lg pl-9 pr-3 py-2 bg-white focus:outline-none focus:border-neutral-400 transition-colors" />
        </div>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Statut</span>
          <select data-testid="filter-statut" value={fStatut} onChange={(e) => setFStatut(e.target.value)} className={selectCls}>
            <option value="">Tous les statuts</option>
            <option value="actif">Actifs</option>
            <option value="pause">En pause</option>
            <option value="prospect">Prospects</option>
            <option value="ancien">Anciens</option>
            <option value="archives">Archivés</option>
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Services</span>
          <select data-testid="filter-services" value={fService} onChange={(e) => setFService(e.target.value)} className={selectCls}>
            <option value="">Tous les services</option>
            <option value="communication">Community Management</option>
            <option value="graphisme">Graphisme</option>
            <option value="web">Web Design</option>
            <option value="les_deux">Les deux</option>
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Trier par</span>
          <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
            <option value="recents">Plus récents</option>
            <option value="nom">Nom A → Z</option>
            <option value="validation">Publications à valider</option>
          </select>
        </label>
        <div className="flex rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid="clients-view-toggle">
          <button data-testid="view-grid" onClick={() => setView("grid")}
            className={`p-2 transition-colors ${view === "grid" ? "bg-[#0B132B] text-white" : "bg-white text-neutral-400 hover:text-neutral-700"}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button data-testid="view-list" onClick={() => setView("list")}
            className={`p-2 transition-colors ${view === "list" ? "bg-[#0B132B] text-white" : "bg-white text-neutral-400 hover:text-neutral-700"}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Liste */}
      {clients === null ? (
        <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-12 text-center" data-testid="clients-empty">
          <p className="font-heading font-bold text-lg text-[#0F172A]">{actifs.length === 0 && !fStatut && !search ? "Aucun client pour le moment" : "Aucun client ne correspond à ces filtres"}</p>
          {actifs.length === 0 && !fStatut && !search && (
            <>
              <p className="text-sm text-neutral-500 mt-1 mb-5">Créez votre premier client pour démarrer : marque, calendrier, idées IA et feed Instagram.</p>
              <button data-testid="empty-add-client" onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#1C2A4F] transition-colors">
                <Plus className="w-4 h-4" /> Créer mon premier client
              </button>
            </>
          )}
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="clients-grid">
          {filtered.map((c, i) => <ClientCard key={c.id} c={c} i={i} onOpen={onOpen} onFiche={setFicheClient} onPonctuel={setFiche} onToggleArchive={onToggleArchive} projetsCount={projetsCount} />)}
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="clients-list">
          {filtered.map((c) => {
            const st = STATUS_BADGE[c.status || "actif"] || STATUS_BADGE.actif;
            return (
              <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => onOpen(c)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 overflow-hidden" style={{ background: c.color }}>
                  {c.brand?.logo_url ? <img src={c.brand.logo_url} className="w-full h-full object-cover" alt="" /> : c.name.slice(0, 1)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{c.name}</p>
                  <p className="text-[11px] text-neutral-400 truncate">{[c.sector, c.city].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="hidden md:flex gap-1.5">
                  {servicePills(c).map((p) => (
                    <span key={p.label} className="text-[10px] font-medium px-2 py-0.5 rounded-full border" style={{ background: p.bg, color: p.text, borderColor: p.border }}>{p.label}</span>
                  ))}
                </div>
                <span className="text-[11px] text-neutral-500 whitespace-nowrap hidden sm:inline">{c.stats?.pending_count ?? 0} à valider · {projetsCount[c.id] || 0} projet(s)</span>
                <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap" style={{ background: st.bg, color: st.text, borderColor: st.border }}>{st.label}</span>
                <ArrowRight className="w-4 h-4 text-neutral-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
      <NewClientModal open={open} setOpen={setOpen} onCreated={(c) => { load(); navigate(`/clients/${c.id}`); }} />
      <ClientFicheModal open={!!ficheClient} onClose={() => setFicheClient(null)} client={ficheClient} onSaved={load} />
      <ClientPonctuelSheet client={fiche} onClose={() => setFiche(null)} onConverted={load} />
    </div>
  );
}
